import api from './api';
import type { HubService, ServiceHubType } from '../constants/serviceHubs';

export async function listHubServices(hubType: ServiceHubType) {
  const res = await api.get(`/admin/hubs/${hubType}/services`);
  return res.data.data as HubService[];
}

export async function getHubService(hubType: ServiceHubType, slug: string) {
  const res = await api.get(`/admin/hubs/${hubType}/services/${slug}`);
  return res.data.data as HubService;
}

export async function createHubService(hubType: ServiceHubType, payload: Partial<HubService>) {
  const res = await api.post(`/admin/hubs/${hubType}/services`, payload);
  return res.data.data as HubService;
}

export async function updateHubService(id: string, payload: Partial<HubService>) {
  const res = await api.put(`/admin/services/${id}`, payload);
  return res.data.data as HubService;
}

export async function toggleHubService(id: string, isActive: boolean) {
  const res = await api.patch(`/admin/services/${id}/status`, { isActive });
  return res.data.data as HubService;
}

export async function deleteHubService(id: string) {
  const res = await api.delete(`/admin/services/${id}`);
  return res.data.data;
}

export async function duplicateHubService(id: string) {
  const res = await api.post(`/admin/services/${id}/duplicate`);
  return res.data.data as HubService;
}

export interface ServiceDashboardData {
  service: { id: string; name: string; slug: string; hubType: string; icon?: string };
  stats: {
    totalOrders: number;
    completed: number;
    cancelled: number;
    running: number;
    pending: number;
    revenue: number;
    commission: number;
    platformEarnings: number;
    providerEarnings: number;
    growthPercent: number;
    bookingsToday: number;
  };
  charts: {
    revenue: { date: string; amount: number }[];
    orders: { date: string; count: number }[];
    peakHours: { hour: string; count: number }[];
    heatMap: { lat: number; lng: number; weight: number }[];
  };
}

export async function getServiceDashboard(id: string, from?: string, to?: string) {
  const res = await api.get(`/admin/services/${id}/dashboard`, { params: { from, to } });
  return res.data.data as ServiceDashboardData;
}

export async function reorderHubServices(hubType: ServiceHubType, orderedIds: string[]) {
  const res = await api.put(`/admin/hubs/${hubType}/services/reorder`, { orderedIds });
  return res.data.data as HubService[];
}

export interface ReviewAnalytics {
  average: number;
  distribution: { star: number; count: number }[];
  topProviders: { name: string; avgRating: number; count: number }[];
}

export async function getServiceReviews(id: string, params?: Record<string, string>) {
  const res = await api.get(`/admin/services/${id}/reviews`, { params });
  const payload = res.data?.data;
  const rows = Array.isArray(payload) ? payload : payload?.rows ?? [];
  return {
    rows: rows as Array<{
      id: string;
      bookingNumber: string;
      customerName: string;
      providerName: string;
      date: string;
      rating: number;
      comment: string;
      customerRating?: number | null;
      customerComment?: string;
      driverRating?: number | null;
      driverComment?: string;
    }>,
    meta: res.data.meta as { total: number; analytics: ReviewAnalytics },
  };
}

export interface EarningsSummary {
  grossRevenue: number;
  commission: number;
  providerEarnings: number;
  taxes: number;
  discounts: number;
  platformEarnings: number;
  refunds: number;
}

export async function getServiceEarnings(id: string, params?: Record<string, string>) {
  const res = await api.get(`/admin/services/${id}/earnings`, { params });
  return res.data.data as {
    rows: Array<{
      id: string; reference: string; date: string; customer: string; provider: string;
      gross: number; commission: number; providerEarnings: number; tax: number;
      discount: number; paymentMethod: string; paymentStatus: string;
    }>;
    summary: EarningsSummary;
    service: { id: string; name: string };
  };
}

export async function exportServiceEarnings(id: string, format: 'csv' | 'pdf', params?: Record<string, string>) {
  const res = await api.get(`/admin/services/${id}/earnings/export`, {
    params: { ...params, format },
    responseType: 'blob',
  });
  return res.data as Blob;
}

export async function estimateServiceFare(id: string, payload: Record<string, unknown>) {
  const res = await api.post(`/admin/services/${id}/estimate-fare`, payload);
  return res.data.data as {
    distanceKm: number;
    durationMin: number;
    fare: Record<string, number>;
  };
}

export async function manualBookRide(id: string, payload: Record<string, unknown>) {
  const res = await api.post(`/admin/services/${id}/manual-booking`, payload);
  return res.data.data;
}

export interface ServiceZoneAssignment {
  _id: string;
  code: string;
  name: string;
  zoneLevel: 'PRIMARY' | 'SECONDARY';
  primaryZoneId?: string;
  isActive: boolean;
  nonServiceZone: boolean;
  enabled: boolean;
  hasPricing: boolean;
}

export async function listServiceZoneAssignments(serviceId: string) {
  const res = await api.get(`/admin/services/${serviceId}/zones`);
  return res.data.data as ServiceZoneAssignment[];
}

export async function setServiceZoneAssignments(serviceId: string, zoneIds: string[]) {
  const res = await api.put(`/admin/services/${serviceId}/zones`, { zoneIds });
  return res.data.data as ServiceZoneAssignment[];
}

export async function searchCustomers(q: string) {
  const res = await api.get('/admin/customers/search', { params: { q } });
  return res.data.data as Array<{ _id: string; firstName?: string; lastName?: string; phone?: string; email?: string }>;
}

export async function listAvailableDrivers() {
  const res = await api.get('/admin/drivers/available');
  return res.data.data as Array<{
    id: string;
    name: string;
    phone?: string;
    email?: string;
    vehicleNumber?: string;
    vehicleType?: string;
    isOnline: boolean;
    isAvailable: boolean;
    rating: number;
  }>;
}

export type NearbyDriverOption = {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  vehicleNumber?: string;
  vehicleType?: string;
  isOnline: boolean;
  isAvailable: boolean;
  rating: number;
  distanceKm: number | null;
  rank: number;
};

export async function listNearbyDrivers(params: {
  lat: number;
  lng: number;
  tripType?: string;
  vehicleTypeCode?: string;
  search?: string;
  limit?: number;
}) {
  const res = await api.get('/admin/drivers/nearby', {
    params: {
      lat: params.lat,
      lng: params.lng,
      tripType: params.tripType,
      vehicleTypeCode: params.vehicleTypeCode,
      q: params.search,
      limit: params.limit,
    },
  });
  return res.data.data as NearbyDriverOption[];
}
